import { Link } from "react-router-dom";
import { ROLES, type Roles, useRoles, ROLE_GUIDS } from "src/context/roles-context";
import { useState } from "react";
import { FaShieldAlt } from "react-icons/fa";
import { MdSupportAgent } from "react-icons/md";
import { mvteHomeQueryOptions } from "src/api/mvte/mvte-home-query-options";
import { useQuery } from "@tanstack/react-query";

type ModuleConfig = {
    moduleId: number;
    name: string;
    moduleRootRoute: string | null;
    requiredRoles: Roles[];
    moduleExternalLink?: string;
};

const GROUP_LINK_PREFIX = "https://portal.azure.com/#view/Microsoft_AAD_IAM/GroupDetailsMenuBlade/~/Overview/groupId/";

const HELPDESK_ADMIN_ROLE: Roles = "MVTECenter.HelpdeskAdmin";

const HELPDESK_ADMIN_MODULE: ModuleConfig = {
    moduleId: -1,
    name: "Who can see Permissions",
    moduleRootRoute: null,
    requiredRoles: [HELPDESK_ADMIN_ROLE],
};

const MODULES_CONFIG: ModuleConfig[] = [
    {
        moduleId: 1,
        name: "Customer Geo Locations",
        moduleRootRoute: "/customer-geo-locations",
        requiredRoles: [],
    },
    {
        moduleId: 2,
        name: "Customer Account Team Management",
        moduleRootRoute: "/customer-account-team-management",
        requiredRoles: [],
    },
    {
        moduleId: 3,
        name: "Customer Service Failure",
        moduleRootRoute: "/customer-service-failures",
        requiredRoles: [
            "ServiceFailure.Standard",
            "ServiceFailure.OpsMgmt",
            "ServiceFailure.CSM",
        ],
    },
    {
        moduleId: 5,
        name: "Order Invoicing",
        moduleRootRoute: "/order-invoicing",
        requiredRoles: ["OrderInvoicing.Write"],
    },
    {
        moduleId: 6,
        name: "PFS Tablet Inventory",
        moduleRootRoute: "/tablet-inventory",
        requiredRoles: ["PFSTabletInventory.ReadWrite"],
    },
    {
        moduleId: 7,
        name: "Tracking Partners",
        moduleRootRoute: "/tracking-partner-management",
        requiredRoles: [
            "TrackingPartnerMgmt.Writer",
            "TrackingPartnerMgmt.Reader",
        ],
    },
    {
        moduleId: 8,
        name: "Pricing Rate Calculator",
        moduleRootRoute: "/rate-calculator",
        requiredRoles: ["BidRateCalculator.Write", "BidRateCalculator.Admin"],
    },
    {
        moduleId: 9,
        name: "Driver Road Test",
        moduleRootRoute: "/driver-road-test",
        requiredRoles: [ROLES["DriverRoadTest.ReadWrite"]],
    },
    {
        moduleId: 10,
        name: "HR Config Management",
        moduleRootRoute: "/hr-config-management",
        requiredRoles: ["HRConfigMgt.Tasker", "HRConfigMgt.Engauge", "HRConfigMgt.EngaugeBeta"],
    },
    {
        moduleId: 11,
        name: "IT Administration",
        moduleRootRoute: "/it-admin",
        requiredRoles: ["ITAdmin.SteelTool.Write", "ITAdmin.TaskerTool.Write"],
    },
];

export default function IndexPage() {
    const roles = useRoles();
    const [showAllModules, setShowAllModules] = useState(false);
    const [accessInfoModule, setAccessInfoModule] = useState<ModuleConfig | null>(null);
    const canViewAccessInfo = roles.checkHasRole("MVTECenter.HelpdeskAdmin");

    const { accessibleModules, inaccessibleModules } = MODULES_CONFIG.reduce<{
        accessibleModules: ModuleConfig[];
        inaccessibleModules: ModuleConfig[];
    }>(
        (acc, curr) => {
            // Some modules don't have permissions setup "properly"
            // If a module has no permissions, then anyone with access
            // to the app can see the module.
            // TODO: Create roles for modules :)
            if (curr.requiredRoles.length === 0) {
                acc.accessibleModules.push(curr);
                return acc;
            }

            const hasRequiredRole = curr.requiredRoles.some((requiredRole) =>
                roles.checkHasRole(requiredRole),
            );

            if (hasRequiredRole) {
                acc.accessibleModules.push(curr);
                return acc;
            }

            acc.inaccessibleModules.push(curr);

            return acc;
        },
        { accessibleModules: [], inaccessibleModules: [] },
    );

    const modulesToDisplay = showAllModules
        ? [...accessibleModules, ...inaccessibleModules]
        : accessibleModules;

    const isInaccessible = (moduleId: number) => {
        return inaccessibleModules.some(
            (module) => module.moduleId === moduleId,
        );
    };

    const accessInfoRoleGuids = accessInfoModule
        ? accessInfoModule.requiredRoles.map((role) => ROLE_GUIDS[role])
        : [];

    const { data: roleGroupData, isLoading: isLoadingGroups } = useQuery({
        ...mvteHomeQueryOptions.getRoleGroupPermsQueryOptions({
            params: { roleGuids: accessInfoRoleGuids },
        }),
        enabled: !!accessInfoModule && accessInfoRoleGuids.length > 0,
    });

    return (
        <div className="px-10 py-10">
            <div className="mb-4 flex justify-end items-center gap-2">
                {showAllModules && canViewAccessInfo && (
                    <button
                        type="button"
                        onClick={() => setAccessInfoModule(HELPDESK_ADMIN_MODULE)}
                        className="text-xs text-amber-400 hover:text-amber-300 hover:underline bg-gray-900/40 px-2 py-2 rounded-md"
                    >
                        Who can see Permissions?
                    </button>
                )}
                <div className="flex items-center gap-2 bg-gray-900/40 p-2 rounded-md">
                    <span className="text-sm text-white">All modules</span>
                    <input
                        type="checkbox"
                        checked={showAllModules}
                        onChange={(e) => setShowAllModules(e.target.checked)}
                        className="toggle border-gray-400 bg-gray-400 checked:bg-green-400 checked:text-green-800 checked:border-green-500"
                    />
                </div>
            </div>

            {modulesToDisplay.length === 0 && (
                <div className="max-w-3xl mx-auto">
                    <div className="card bg-gray-800 border border-gray-700 shadow-lg">
                        <div className="card-body p-8">
                            <div className="flex flex-col items-center text-center mb-6">
                                <div className="bg-amber-500/20 p-4 rounded-full mb-4">
                                    <FaShieldAlt className="h-12 w-12 text-amber-400" />
                                </div>
                                <h2 className="card-title text-2xl font-bold text-white mb-2">
                                    Access Restricted
                                </h2>
                                <p className="text-gray-300 max-w-xl">
                                    You currently don't have permissions to view
                                    any modules in this system.
                                </p>
                            </div>

                            <div className="bg-gray-900/50 rounded-lg p-6 mb-6">
                                <h3 className="text-lg font-medium text-white mb-4">
                                    Need access? Here's how to get it:
                                </h3>
                                <div className="space-y-4">
                                    <div className="flex items-start gap-3">
                                        <div className="bg-gray-700 p-2 rounded-md">
                                            <MdSupportAgent className="h-5 w-5 text-green-400" />
                                        </div>
                                        <div>
                                            <h4 className="text-white font-medium">
                                                Helpdesk
                                            </h4>
                                            <p className="text-gray-400 text-sm">
                                                Open a ticket through our SysAid
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {modulesToDisplay.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full">
                    {modulesToDisplay.map((module) => {
                        const noAccess = isInaccessible(module.moduleId);
                        return (
                            <div
                                key={module.moduleId}
                                className={`card ${
                                    noAccess
                                        ? "bg-gray-700/70 border border-dashed border-gray-600"
                                        : "bg-gray-700"
                                }`}
                            >
                                <div className="card-body">
                                    <div className="card-title text-white mb-auto">
                                        {module.name}
                                    </div>

                                    <div>
                                        {noAccess && (
                                            <div className="flex items-center gap-1.5 text-xs text-amber-400 mb-2">
                                                <FaShieldAlt className="h-3.5 w-3.5" />
                                                <span>No access</span>
                                            </div>
                                        )}

                                        {showAllModules && canViewAccessInfo && (
                                            <div className="flex items-center gap-1.5 text-xs text-amber-400 mb-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setAccessInfoModule(module)}
                                                    className="hover:text-amber-300 hover:underline"
                                                >
                                                    Who has Access?
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="card-actions">
                                        {module.moduleRootRoute ? (
                                            <Link
                                                to={module.moduleRootRoute}
                                                className={`btn w-full ${
                                                    noAccess
                                                        ? "btn-secondary opacity-70"
                                                        : "btn-primary"
                                                }`}
                                                onClick={(e) => {
                                                    if (noAccess)
                                                        e.preventDefault();
                                                }}
                                            >
                                                Open
                                            </Link>
                                        ) : module.moduleExternalLink ? (
                                            <a
                                                href={module.moduleExternalLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`btn w-full ${
                                                    noAccess
                                                        ? "btn-secondary opacity-70"
                                                        : "btn-primary"
                                                }`}
                                                onClick={(e) => {
                                                    if (noAccess) e.preventDefault();
                                                }}
                                            >
                                                Open
                                            </a>
                                        ) : (
                                            <button
                                                className="btn btn-secondary w-full opacity-70"
                                                disabled
                                            >
                                                Open
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {accessInfoModule && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
                    onClick={() => setAccessInfoModule(null)}
                >
                    <div
                        className="bg-gray-800 border border-gray-700 rounded-lg shadow-xl w-full max-w-md p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-lg font-semibold text-white">
                                Who has access: {accessInfoModule.name}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setAccessInfoModule(null)}
                                className="text-gray-400 hover:text-white"
                            >
                                ✕
                            </button>
                        </div>

                        {accessInfoModule.requiredRoles.length === 0 ? (
                            <p className="text-gray-300 text-sm">
                                No specific role required — anyone with app access can view this module.
                            </p>
                        ) : isLoadingGroups ? (
                            <p className="text-gray-400 text-sm">Loading groups...</p>
                        ) : (
                            <ul className="space-y-3">
                                {accessInfoModule.requiredRoles.map((role) => {
                                    const roleGuid = ROLE_GUIDS[role];
                                    const groupsForRole =
                                        roleGroupData?.Value.filter(
                                            (a) => a.AppRoleId === roleGuid,
                                        ) ?? [];

                                    return (
                                        <li
                                            key={role}
                                            className="text-sm text-gray-200 bg-gray-900/50 rounded px-3 py-2"
                                        >
                                            <div className="font-medium mb-1">{role}</div>
                                            {groupsForRole.length === 0 ? (
                                                <span className="text-gray-400 text-xs">
                                                    No groups found
                                                </span>
                                            ) : (
                                                <ul className="space-y-1 pl-2">
                                                    {groupsForRole.map((group) => (
                                                        <li key={group.PrincipalId}>
                                                            <a
                                                                href={`${GROUP_LINK_PREFIX}${group.PrincipalId}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-amber-400 hover:text-amber-300 underline"
                                                            >
                                                                {group.PrincipalDisplayName}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}